import React, { useEffect, useState } from 'react';
import BasicModal from '../../atoms/BasicModal';
import {
  Button,
  DatePicker,
  Switch,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Typography from '@HomaHealth/ui-component-library/lib/_patient-access-components/atoms/Typography';
import { CheckBox, patientAccessTheme } from '@HomaHealth/ui-component-library';
import { useStores } from '../../../stores';
import { useNavigate } from 'react-router-dom';
import { REPORTS_PPT } from '../../../core-utils/routes';
import { observer } from 'mobx-react';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import { commonDropDownProps } from '../../atoms/CohortAnalysisModal';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import Info from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import { AggregationParameter } from '../../../stores/QuaterlyReportStore';
import { formatDate } from '../../../core-utils/helper-utils/helper';
import {
  formatPlanPeriodDates,
  PlanDates,
} from '../../../core-utils/custom-hooks/PlanDates';
import moment from 'moment';
import { toJS } from 'mobx';

interface QuaterlyReportModalProps {
  open: boolean;
  handleClose: () => void;
  isQuaterlyReport: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  saveBtnText?: string;
  disableIncurredDate?: boolean;
  disablePaidDate?: boolean;
  isModal?: boolean;
  showNoDateFilterOption?: boolean;
  handleSave?: () => void;
}
type Dates = {
  startDate: string;
  endDate: string;
};

export const getPlanDate = (plan: PlanDates) => {
  if (!plan?.paid_start_date || !plan?.paid_end_date) {
    return '';
  }
  return `${formatDate(plan.paid_start_date, 'MMMM YYYY')} - ${formatDate(
    plan.paid_end_date,
    'MMMM YYYY',
  )}`;
};
const updatePlanPeriod = (
  plan: any[],
  selectedPlanIndex: number,
  paidStartDate: string,
  setPlanPeriod: React.Dispatch<React.SetStateAction<string>>,
  paidEndDate?: string,
  type?: string,
) => {
  if (paidStartDate && paidEndDate) {
    setPlanPeriod(
      moment(plan[selectedPlanIndex]?.paid_start_date).isSame(paidStartDate) &&
        moment(plan[selectedPlanIndex]?.paid_end_date).isSame(paidEndDate)
        ? getPlanDate(plan[selectedPlanIndex])
        : '',
    );
    return;
  }

  setPlanPeriod(
    moment(
      type === 'startDate'
        ? plan[selectedPlanIndex]?.paid_start_date
        : plan[selectedPlanIndex]?.paid_end_date,
    ).isSame(paidStartDate)
      ? getPlanDate(plan[selectedPlanIndex])
      : '',
  );
};

const QuaterlyReportModal: React.FC<QuaterlyReportModalProps> = observer(
  ({
    open,
    handleClose,
    isQuaterlyReport = false,
    saveBtnText,
    setOpen,
    disableIncurredDate = false,
    disablePaidDate = false,
    isModal = true,
    showNoDateFilterOption = false,
    handleSave,
  }) => {
    const { classes } = makeStyles()({
      root: {
        minHeight: '360px',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        paddingRight: '0.5rem',
        paddingBottom: '250px',
      },
      incurredContainer: {
        background: '#FBFBFE',
        border: `1px solid ${patientAccessTheme.palette.grey[100]}`,
        borderRadius: '0.5rem',
        padding: '0.5rem',
      },
      selectDatesInfo: {
        height: '37px',
        background: patientAccessTheme.palette.grey[100],
        border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
      },
      paidTitleContainer: {
        display: 'flex',
        justifyContent: 'space-between',
      },
      selectFields: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '0.5rem',
        backgroundColor: patientAccessTheme.palette.status.white,
        height: '48px',
        overflow: 'hidden',
      },
    })();

    const quaterlyReportStore = useStores().quaterlyReportStore;
    const {
      minIncurred,
      maxIncurred,
      minPaid,
      maxPaid,
      planDates,
      selectedPlanIndex,
    } = quaterlyReportStore;
    const navigate = useNavigate();

    const [incurredDate, setIncurredDate] = useState<Dates>({
      startDate: quaterlyReportStore.incurredStartDate,
      endDate: quaterlyReportStore.incurredEndDate,
    });
    const [paidDate, setPaidDate] = useState<Dates>({
      startDate: quaterlyReportStore.paidStartDate,
      endDate: quaterlyReportStore.paidEndDate,
    });
    const [checkedIncurred, setCheckedIncurred] = useState<boolean>(
      quaterlyReportStore.isIncurredDate,
    );
    const [checkedPaid, setCheckedPaid] = useState<boolean>(
      quaterlyReportStore.isPaidDate,
    );
    const [periodType, setPeriodType] = useState<AggregationParameter>(
      quaterlyReportStore.aggregationParameter,
    );
    const [planPeriod, setPlanPeriod] = useState<string>('');
    const [noDateFilter, setNoDateFilter] = useState<boolean>(
      quaterlyReportStore.noDateFilter,
    );

    const dropdownOptions = ['Incurred Date', 'Paid Date'];
    const dropdownValues = ['incurred', 'paid'];

    const dropDownOptionsPlanPeriod: string[] = planDates.map((plan) =>
      getPlanDate(plan),
    );

    const handleInputChange = (value: any, name: string, incurred: boolean) => {
      const planIndex = planDates.findIndex((plan) => plan.current);
      setNoDateFilter(false);
      if (incurred) {
        setIncurredDate((prevState: any) => ({
          ...prevState,
          [name]: formatDate(value, 'YYYY-MM-DD'),
        }));
      } else {
        updatePlanPeriod(
          toJS(planDates),
          selectedPlanIndex !== -1 ? selectedPlanIndex : planIndex,
          value,
          setPlanPeriod,
          '',
          name,
        );
        setPaidDate((prevState: any) => ({
          ...prevState,
          [name]: formatDate(value, 'YYYY-MM-DD'),
        }));
      }
    };
    const handleSwitch = (
      setChecked: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      setNoDateFilter(false);
      setChecked((prev) => !prev);
    };
    const handleSaveBtnDisabled = () => {
      if (incurredDate.startDate === '' || incurredDate.endDate === '') {
        return true;
      }
      return false;
    };
    const handleSaveBtn = () => {
      const planIndex = dropDownOptionsPlanPeriod.findIndex(
        (plan) => plan === planPeriod,
      );
      quaterlyReportStore.setIncurredStartDate(incurredDate.startDate);
      quaterlyReportStore.setIncurredEndDate(incurredDate.endDate);
      quaterlyReportStore.setPaidStartDate(paidDate.startDate);
      quaterlyReportStore.setPaidEndDate(paidDate.endDate);
      quaterlyReportStore.setAggregationParameter(periodType);
      quaterlyReportStore.setIsIncurredDate(checkedIncurred);
      quaterlyReportStore.setIsPaidDate(checkedPaid);
      quaterlyReportStore.setSelectedPlanIndex(planIndex);
      quaterlyReportStore.setNoDateFilter(noDateFilter);

      setOpen(false);
      handleSave && handleSave();
      isQuaterlyReport && navigate(REPORTS_PPT);
    };

    const handlePlanPeriodChange = (event: any) => {
      const value = event.target.value;
      const planIndex = dropDownOptionsPlanPeriod.findIndex(
        (plan) => plan === value,
      );
      const plan = planDates[planIndex];

      setPlanPeriod(value as string);
      setIncurredDate({
        startDate: formatPlanPeriodDates(plan).incurred_start_date,
        endDate: formatPlanPeriodDates(plan).incurred_end_date,
      });
      setPaidDate({
        startDate: formatPlanPeriodDates(plan).paid_start_date,
        endDate: formatPlanPeriodDates(plan).paid_end_date,
      });
      setNoDateFilter(false);
    };

    const getDropdownOptionsAndValue = (type: string) => {
      if (type === 'option') {
        if (checkedIncurred && checkedPaid) {
          return dropdownOptions;
        } else if (checkedIncurred && !checkedPaid) {
          return ['Incurred Date'];
        } else if (!checkedIncurred && checkedPaid) {
          return ['Paid Date'];
        } else {
          return ['Incurred Date'];
        }
      } else {
        if (checkedIncurred && checkedPaid) {
          return dropdownValues;
        } else if (checkedIncurred && !checkedPaid) {
          return ['incurred'];
        } else if (!checkedIncurred && checkedPaid) {
          return ['paid'];
        } else {
          return ['incurred'];
        }
      }
    };
    const getModalTitle = () => {
      let title: string = 'Select ';

      if (disableIncurredDate) {
        title += ' Paid Period';
      } else if (disablePaidDate) {
        title += ' Incurred Period';
      } else {
        title += ' Incurred Period and Paid Period';
      }
      return title;
    };

    useEffect(() => {
      if (open) {
        setIncurredDate({
          startDate: quaterlyReportStore.incurredStartDate,
          endDate: quaterlyReportStore.incurredEndDate,
        });
        setPaidDate({
          startDate: quaterlyReportStore.paidStartDate,
          endDate: quaterlyReportStore.paidEndDate,
        });
        setPeriodType(quaterlyReportStore.aggregationParameter);
        const planIndex = planDates.findIndex((plan) => plan.current);

        if (selectedPlanIndex !== -1) {
          // setPlanPeriod(getPlanDate(planDates[selectedPlanIndex]));
          updatePlanPeriod(
            planDates,
            selectedPlanIndex,
            quaterlyReportStore.paidStartDate,
            setPlanPeriod,
            quaterlyReportStore.paidEndDate,
          );
        } else {
          updatePlanPeriod(
            planDates,
            planIndex,
            quaterlyReportStore.paidStartDate,
            setPlanPeriod,
            quaterlyReportStore.paidEndDate,
          );
        }
      }
    }, [open]);

    useEffect(() => {
      if (checkedIncurred && !checkedPaid) {
        setPeriodType('incurred');
      } else if (!checkedIncurred && checkedPaid) {
        setPeriodType('paid');
      } else if (checkedIncurred && checkedPaid) {
        setPeriodType('paid'); // Default to paid if both are enabled
      } else {
        setPeriodType('incurred');
      }
    }, [checkedIncurred, checkedPaid]);

    const ModalContent = () => (
      <Grid container className={classes.root}>
        {disableIncurredDate &&
          disablePaidDate &&
          !checkedIncurred &&
          !checkedPaid && (
            <Grid
              container
              className={classes.incurredContainer}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Info />
              <Typography
                fontSize="0.9rem"
                fontType="regular"
                textColor={patientAccessTheme.palette.text.highemphasis}
                margin="0 0.5rem"
              >
                Visuals will be dispalyed based on Plan Period:{' '}
                <b>{formatDate(incurredDate.startDate, 'YYYY-MM-DD')}</b>
                {' to '}
                <b>{formatDate(incurredDate.endDate, 'YYYY-MM-DD')} </b>
              </Typography>
            </Grid>
          )}
        {planDates.length > 1 && (
          <Grid>
            <ReportCreateNewLabelField
              label={'Select Plan Period'}
              width="100%"
              component={
                <Dropdown
                  label={'Select Period Type'}
                  dropDownOptions={dropDownOptionsPlanPeriod}
                  dropDownValues={dropDownOptionsPlanPeriod}
                  value={planPeriod}
                  selectStyles={{
                    ...commonDropDownProps.selectStyles,
                    width: '310px',
                  }}
                  labelTop="10px"
                  handleChange={handlePlanPeriodChange}
                />
              }
            />
          </Grid>
        )}

        {!disableIncurredDate && (
          <Grid className={classes.incurredContainer}>
            <Grid className={classes.paidTitleContainer}>
              <Typography
                fontSize="1rem"
                fontType="medium"
                textColor={patientAccessTheme.palette.text.highemphasis}
                margin=" 0 0 0.5rem 0"
              >
                Incurred Date:
              </Typography>
              <Switch
                checked={checkedIncurred}
                handleChange={() => {
                  handleSwitch(setCheckedIncurred);
                }}
              />
            </Grid>
            {checkedIncurred && (
              <Grid style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Typography fontSize="12px" fontType="medium" lineHeight="177%" textColor={patientAccessTheme.palette.text.lowemphasis}>Start Date</Typography>
                <DatePicker
                  width="234px"
                  height="39px"
                  views={['year', 'month']}
                  placeholder="MMM YYYY"
                  format="MMMM yyyy"
                  selectedDate={incurredDate.startDate ? moment(incurredDate.startDate).toDate() : (null as any)}
                  minDate={minIncurred ? moment(minIncurred).toDate() : (null as any)}
                  maxDate={maxIncurred ? moment(maxIncurred).toDate() : (null as any)}
                  handleChange={(date: Date) =>
                    handleInputChange(date, 'startDate', true)
                  }
                  disablePast={false}
                  popoverPaperStyles={{ zIndex: 2000 }}
                />
                <Typography fontSize="12px" fontType="medium" lineHeight="177%" textColor={patientAccessTheme.palette.text.lowemphasis}>End Date</Typography>
                <DatePicker
                  width="234px"
                  height="39px"
                  views={['year', 'month']}
                  placeholder="MMM YYYY"
                  format="MMMM yyyy"
                  selectedDate={incurredDate.endDate ? moment(incurredDate.endDate).toDate() : (null as any)}
                  minDate={incurredDate.startDate ? moment(incurredDate.startDate).toDate() : (null as any)}
                  maxDate={maxIncurred ? moment(maxIncurred).toDate() : (null as any)}
                  handleChange={(date: Date) =>
                    handleInputChange(date, 'endDate', true)
                  }
                  disablePast={false}
                  popoverPaperStyles={{ zIndex: 2000 }}
                />
              </Grid>
            )}
          </Grid>
        )}

        {!disablePaidDate && (
          <Grid className={classes.incurredContainer}>
            <Grid className={classes.paidTitleContainer}>
              <Typography
                fontSize="1rem"
                fontType="medium"
                textColor={patientAccessTheme.palette.text.highemphasis}
                margin=" 0 0 0.5rem 0"
              >
                Paid Date:
              </Typography>
              <Switch
                checked={checkedPaid}
                handleChange={() => {
                  handleSwitch(setCheckedPaid);
                }}
              />
            </Grid>
            {checkedPaid && (
              <Grid style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Typography fontSize="12px" fontType="medium" lineHeight="177%" textColor={patientAccessTheme.palette.text.lowemphasis}>Start Date</Typography>
                <DatePicker
                  width="234px"
                  height="39px"
                  views={['year', 'month']}
                  placeholder="MMM YYYY"
                  format="MMMM yyyy"
                  selectedDate={paidDate.startDate ? moment(paidDate.startDate).toDate() : (null as any)}
                  minDate={minPaid ? moment(minPaid).toDate() : (null as any)}
                  maxDate={maxPaid ? moment(maxPaid).toDate() : (null as any)}
                  handleChange={(date: Date) =>
                    handleInputChange(date, 'startDate', false)
                  }
                  disablePast={false}
                  popoverPaperStyles={{ zIndex: 2000 }}
                />
                <Typography fontSize="12px" fontType="medium" lineHeight="177%" textColor={patientAccessTheme.palette.text.lowemphasis}>End Date</Typography>
                <DatePicker
                  width="234px"
                  height="39px"
                  views={['year', 'month']}
                  placeholder="MMM YYYY"
                  format="MMMM yyyy"
                  selectedDate={paidDate.endDate ? moment(paidDate.endDate).toDate() : (null as any)}
                  minDate={paidDate.startDate ? moment(paidDate.startDate).toDate() : (null as any)}
                  maxDate={maxPaid ? moment(maxPaid).toDate() : (null as any)}
                  handleChange={(date: Date) =>
                    handleInputChange(date, 'endDate', false)
                  }
                  disablePast={false}
                  popoverPaperStyles={{ zIndex: 2000 }}
                />
              </Grid>
            )}
          </Grid>
        )}
        {disablePaidDate && disableIncurredDate && (
          <Grid>
            <ReportCreateNewLabelField
              label={'Calculation By'}
              width="100%"
              component={
                <Dropdown
                  label={'Select Period Type'}
                  dropDownOptions={getDropdownOptionsAndValue('option')}
                  dropDownValues={getDropdownOptionsAndValue('value')}
                  value={periodType}
                  selectStyles={{
                    ...commonDropDownProps.selectStyles,
                    width: '230px',
                  }}
                  labelTop="10px"
                  handleChange={(e) => {
                    setPeriodType(e.target.value as AggregationParameter);
                  }}
                />
              }
            />
          </Grid>
        )}
        {showNoDateFilterOption && (
          <Grid className={classes.selectFields}>
            <CheckBox
              checked={noDateFilter}
              id=""
              name=""
              color="primary"
              handleChange={() => {
                setNoDateFilter(!noDateFilter);
              }}
            />
            <Typography
              textColor={patientAccessTheme.palette.text.highemphasis}
              fontSize="14px"
              lineHeight="20px"
              fontType="medium"
            >
              {'No Filter On Dates'}
            </Typography>
          </Grid>
        )}
      </Grid>
    );

    return isModal ? (
      <BasicModal
        width="950px"
        max_height="800px"
        open={open}
        handleClose={handleClose}
        title={getModalTitle()}
        saveBtnText={saveBtnText || 'Open Report'}
        isSaveButton={true}
        isCloseBtn={true}
        saveBtnAction={handleSaveBtn}
        saveBtnDisabled={handleSaveBtnDisabled()}
      >
        <ModalContent />
      </BasicModal>
    ) : (
      <Grid>
        <ModalContent />
        <Grid style={{ display: 'flex', gap: '1rem', marginLeft: 'auto' }}>
          <Button
            color="primary"
            variant="contained"
            height="36px"
            width="fit-content"
            label={saveBtnText}
            onClick={handleSaveBtn}
            disabled={handleSaveBtnDisabled()}
            styleProps={{
              minWidth: '150px',
            }}
          />
        </Grid>
      </Grid>
    );
  },
);

export default QuaterlyReportModal;
